/**
 * TargaLookup — Input targa con lookup ottimista + fallback manuale.
 * Al momento (Fase 5.1) il backend restituisce sempre {found:false} — è uno
 * stub predisposto per una futura integrazione API Motorizzazione/ACI/ANIA.
 *
 * Props:
 *  - value       : { targa, marca, modello, alimentazione, anno }
 *  - onChange    : (partial) => void
 *  - required    : bool
 *  - dataTestId  : string  (default "targa-lookup")
 */
import { useCallback, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function normalizeTarga(raw) {
  return (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
}

function isValidItalianTarga(t) {
  // Pattern moderno standard: 2 lettere + 3 cifre + 2 lettere (AB123CD)
  return /^[A-Z]{2}[0-9]{3}[A-Z]{2}$/.test(t)
      // ciclomotori/rimorchi/veicoli speciali: pattern più tolleranti (5-8 alfanum)
      || /^[A-Z0-9]{5,8}$/.test(t);
}

export function TargaLookup({ value, onChange, required = false, dataTestId = "targa-lookup" }) {
  const v = value || {};
  const [loading, setLoading] = useState(false);
  const [lookupStatus, setLookupStatus] = useState(null); // "found" | "not_found" | "error" | null
  const [showManual, setShowManual] = useState(false);

  const doLookup = useCallback(async (targa) => {
    if (!isValidItalianTarga(targa)) return;
    setLoading(true);
    setLookupStatus(null);
    try {
      const r = await axios.get(`${API}/convenzioni/vehicle/lookup`, {
        params: { targa },
      });
      const data = r.data || {};
      if (data.found) {
        onChange({
          targa,
          marca: data.marca || "",
          modello: data.modello || "",
          alimentazione: data.alimentazione || "",
          anno: data.anno || "",
        });
        setLookupStatus("found");
      } else {
        setLookupStatus("not_found");
        setShowManual(true);
      }
    } catch {
      setLookupStatus("error");
      setShowManual(true);
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  const handleTargaChange = (e) => {
    const norm = normalizeTarga(e.target.value);
    onChange({ ...v, targa: norm });
    if (isValidItalianTarga(norm)) doLookup(norm);
  };

  return (
    <div className="space-y-2" data-testid={dataTestId}>
      <Label className="text-sm text-[#4A5E51]">
        Targa {required && <span className="text-[#7A1D1D]">*</span>}
      </Label>
      <div className="relative">
        <Input
          type="text"
          value={v.targa || ""}
          onChange={handleTargaChange}
          placeholder="AB123CD"
          maxLength={8}
          className="rounded-lg border-[#E5E2DB] h-10 text-sm pr-9 uppercase font-mono"
          data-testid={`${dataTestId}-targa-input`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading && <Loader2 className="w-4 h-4 text-[#4A5E51] animate-spin" />}
          {!loading && lookupStatus === "found" &&
            <CheckCircle2 className="w-4 h-4 text-green-700" />}
          {!loading && lookupStatus === "not_found" &&
            <AlertCircle className="w-4 h-4 text-amber-600" />}
        </div>
      </div>

      {/* Chip veicolo (se trovato) */}
      {lookupStatus === "found" && v.marca && (
        <div className="text-xs bg-green-50 border border-green-200 rounded-lg
                          px-3 py-2 text-green-900"
              data-testid={`${dataTestId}-chip`}>
          🚗 <strong>{v.marca} {v.modello}</strong>
          {v.alimentazione && ` · ${v.alimentazione}`}
          {v.anno && ` · ${v.anno}`}
        </div>
      )}

      {/* Fallback manuale */}
      {(lookupStatus === "not_found" || lookupStatus === "error" || showManual) && (
        <div className="mt-2 p-3 bg-amber-50/60 border border-amber-200 rounded-lg space-y-2"
              data-testid={`${dataTestId}-manual`}>
          <p className="text-xs text-amber-900">
            Non riusciamo a recuperare automaticamente i dati veicolo.
            Puoi inserirli manualmente (facoltativi ma utili per un preventivo più accurato).
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="text"
              value={v.marca || ""}
              onChange={(e) => onChange({ ...v, marca: e.target.value })}
              placeholder="Marca (es. Fiat)"
              maxLength={30}
              className="h-9 text-sm"
              data-testid={`${dataTestId}-manual-marca`}
            />
            <Input
              type="text"
              value={v.modello || ""}
              onChange={(e) => onChange({ ...v, modello: e.target.value })}
              placeholder="Modello (es. Panda)"
              maxLength={40}
              className="h-9 text-sm"
              data-testid={`${dataTestId}-manual-modello`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TargaLookup;
